import axios from 'axios';
import { saveInteractionData } from '../helpers/interactionHelper';
import { useAuthStore } from '../stores/auth';

/**
 * Downloading a hub file: fetch it through the access gate, hand the bytes to
 * the browser, record the download. Every surface that offers a download goes
 * through here so the analytics payload never drifts between them.
 */

const getFilenameFromUrl = (url) => {
  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname || '';
    const last = pathname.substring(pathname.lastIndexOf('/') + 1);
    return decodeURIComponent(last || '');
  } catch (e) {
    return '';
  }
};

const stripExtension = (name) => {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(0, idx) : name;
};

const getExtension = (name) => {
  if (!name) return '';
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(idx + 1).toLowerCase() : '';
};

// Mime types whose subtype is no extension ("vnd.openxmlformats-...", "plain").
const MIME_EXTENSIONS = {
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'image/jpeg': 'jpg',
  'image/svg+xml': 'svg',
  'audio/mpeg': 'mp3',
};

/**
 * The name the browser saves under. `name` is stored without its extension,
 * so one is put back from the stored file, or from the mime type.
 */
export const downloadFileName = (resource) => {
  const name = (resource?.name || '').trim() || stripExtension(getFilenameFromUrl(resource?.src || '')) || 'file';
  if (getExtension(name)) return name;
  const extension = getExtension(getFilenameFromUrl(resource?.src || ''))
    || MIME_EXTENSIONS[String(resource?.type || '').split(';')[0].trim().toLowerCase()]
    || (resource?.type ? String(resource.type).split('/').pop() : '');
  return extension ? `${name}.${extension}` : name;
};

export const buildResourceProperties = (resource) => {
  const displayName = resource?.name || '';
  const filenameFromSrc = getFilenameFromUrl(resource?.src || '') || displayName;
  const documentName = stripExtension(displayName);
  const extension = getExtension(filenameFromSrc) || (resource?.type ? String(resource.type).split('/').pop() : '');

  return {
    content_name: displayName,
    resource_name: displayName,
    title: resource?.session_title || documentName || displayName,
    name: displayName,
    filename: filenameFromSrc,
    resource_title: resource?.session_title || documentName,
    document_name: documentName,
    extension,
    url: resource?.src || undefined,
  };
};

/**
 * A blob URL belongs to this app's origin, so only types a browser renders
 * without running anything may open in a tab. Resources accept SVG, and an
 * SVG can carry a script that would then run as the signed-in viewer.
 * Everything else is saved instead.
 */
const PREVIEWABLE = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'text/plain']);

/** The type a tab may render this as, or null when it must be saved. */
const inlineType = (resource) => {
  const type = String(resource?.type || '').split(';')[0].trim().toLowerCase();
  return PREVIEWABLE.has(type) ? type : null;
};

const saveFile = (url, resource) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = downloadFileName(resource);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export default function useResourceDownload() {
  const { userData } = useAuthStore();

  /**
   * The bytes, through the access gate. `src` is an identifier here, not a
   * fetch target: the bucket refuses reads of hub files from outside the
   * platform's own AWS account (XN-4595), so only the API, reading as the
   * hub, can turn it into the file. A session's files come without `src`
   * (XN-4745) and are fetched by id, through the same per-file gate.
   */
  const fetchBlob = async (resource, type) => {
    const api = window.store.event_backend_api;
    const response = resource.src
      ? await axios.get(`${api}/resource-fetch`, { params: { url: resource.src }, responseType: 'blob' })
      : await axios.get(`${api}/resources/${resource.id}/download`, { responseType: 'blob' });

    return new Blob([response.data], { type: type ?? response.headers['content-type'] });
  };

  /**
   * Throws the axios error; the caller owns the toast and any refetch.
   * Resolves to what saveInteractionData did: `false` when the download was
   * not logged (owner/staff), so a counter on screen can stay honest.
   */
  const download = async (resource) => {
    const url = window.URL.createObjectURL(await fetchBlob(resource));
    saveFile(url, resource);
    window.URL.revokeObjectURL(url);

    return saveInteractionData(userData, null, 'resources', resource.id, buildResourceProperties(resource));
  };

  /**
   * Open in a new tab, through the same gate, when the type is one a tab may
   * render; otherwise save it. The tab is opened before the fetch, on the
   * click itself — opened after the await it is a pop-up to the browser and
   * gets blocked — and is pointed at the bytes once they land. The blob is
   * typed from the record, never from the response, so nothing the gate
   * returns can widen what the tab renders. Nothing is logged: opening a
   * file is not a download.
   *
   * Throws the axios error; the caller owns the toast and any refetch.
   */
  const open = async (resource) => {
    const type = inlineType(resource);
    const tab = type ? window.open('', '_blank') : null;
    if (type && !tab) throw new Error('The browser blocked the new tab.');

    try {
      const url = window.URL.createObjectURL(await fetchBlob(resource, type ?? 'application/octet-stream'));

      if (!type) {
        saveFile(url, resource);
        window.URL.revokeObjectURL(url);
        return;
      }

      tab.location.href = url;
      // Long enough for the tab to load it; kept forever, every open would leak a copy.
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      tab?.close();
      throw error;
    }
  };

  /**
   * The bytes for the room's own viewer (a phone's Files → View), through the
   * same gate and typed from the record as open() types them; null for a type
   * that may not render inline, which the viewer offers to download instead.
   * The caller revokes the URL. Nothing is logged: viewing is not a download.
   */
  const preview = async (resource) => {
    const type = inlineType(resource);
    if (!type) return null;

    return { type, url: window.URL.createObjectURL(await fetchBlob(resource, type)) };
  };

  return { download, open, preview };
}
